/**
 * Questions tab — what is actually in this bench, one line per question.
 *
 * The Bench tab lists solutions, which is the wrong altitude for arriving: a
 * bench is normally many solutions to the SAME question, so fifteen strips with
 * identical headers say nothing about coverage. This says it in one line each —
 * how many solutions, from how many students, how many prompt executions ran
 * against them, and how far the judging has actually got.
 *
 * What it deliberately does NOT show is what the analyses concluded about the
 * students (correct / incorrect / score). That is a property of the solution —
 * a wrong answer *should* score 0 — so aggregating it says something about who
 * was benched, not about the models under test. The one score-derived figure
 * kept is disagreement BETWEEN models on one fixed solution, which is a model
 * signal and the only triage cue that exists before anyone has judged anything.
 */

import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { DataTable, type ColumnDef, type RowMenuItem, type SortState } from '@shared-ui/components';
import { LatexText } from '@shared-ui/latex';
import { adminApi } from '../../api/adminApi';
import type { VerdictValue } from '../../types/scenarioEvals';
import { rollupByQuestion, type QuestionRollup, type VerdictTally } from './benchQuestionRollup';
import { AddRowsModal } from './AddRowsModal';
import './BenchTab.css';

export function BenchQuestions({ datasetId, onOpenQuestion }: {
    datasetId: string;
    onOpenQuestion?: (questionId: string) => void;
}) {
    const [sort, setSort] = useState<SortState>({ sortBy: 'disputed', sortOrder: 'desc' });
    // `''` opens the picker with nothing chosen; a question id opens it ready to
    // extend that question.
    const [adding, setAdding] = useState<string | null>(null);
    const qc = useQueryClient();

    const { data: matrix, isLoading, error } = useQuery({
        // Same query key the Bench tab uses, so arriving here after judging
        // shows the updated counts instead of refetching the identical matrix.
        queryKey: ['benchMatrix', datasetId, true],
        queryFn: () => adminApi.getBenchMatrix(datasetId, true),
        enabled: !!datasetId,
    });

    const questions = useMemo(
        () => sortQuestions(rollupByQuestion(matrix?.rows ?? []), sort),
        [matrix, sort],
    );

    if (error) return <p className="bench-error">{String(error)}</p>;

    const totals = questions.reduce((t, q) => ({
        solutions: t.solutions + q.solutions,
        cells: t.cells + q.cells,
        judged: t.judged + q.human.n,
    }), { solutions: 0, cells: 0, judged: 0 });

    const columns: ColumnDef<QuestionRollup>[] = [
        {
            key: 'question',
            header: 'Question',
            mobile: 'title',
            render: q => (
                <div className="bench-qcell">
                    <LatexText text={q.text || q.questionId} />
                    <div className="bench-row-meta">
                        {q.subject && <span className="bench-pill">{q.subject}</span>}
                        {q.questionType && <span className="bench-pill">{q.questionType}</span>}
                        {q.labeled === 0
                            ? <span className="bench-pill unlabeled">unlabeled</span>
                            : <span className="bench-pill">{q.labeled}/{q.solutions} labeled</span>}
                        {q.promptVersions > 1 && (
                            <span className="bench-pill warn"
                                title={`Assessment cells here span ${q.promptVersions} prompt versions. Compare within a solution; comparing across solutions compares prompts as much as models.`}>
                                {q.promptVersions} prompt versions
                            </span>
                        )}
                    </div>
                </div>
            ),
        },
        {
            key: 'users',
            header: 'Students',
            sortable: true,
            className: 'bench-num',
            render: q => q.users || <span className="bench-muted">—</span>,
        },
        {
            key: 'solutions',
            header: 'Solutions',
            sortable: true,
            className: 'bench-num',
            render: q => q.solutions,
        },
        {
            key: 'prompts',
            header: 'Prompts',
            sortable: true,
            className: 'bench-num',
            mobileLabel: 'Prompts',
            render: q => (
                <span title={q.promptVersions > 1
                    ? `Distinct prompts. The assessment prompt alone spans ${q.promptVersions} versions.`
                    : 'Distinct prompts that have run against this question.'}>
                    {q.prompts || <span className="bench-muted">—</span>}
                </span>
            ),
        },
        {
            key: 'models',
            header: 'Models',
            sortable: true,
            className: 'bench-num',
            mobileLabel: 'Models',
            render: q => (q.models > 1
                ? q.models
                : <span className="bench-muted" title="One model has run here, so there is nothing to compare it against.">
                    {q.models || '—'}
                </span>),
        },
        {
            key: 'cells',
            header: 'Executions',
            sortable: true,
            className: 'bench-num',
            render: q => (
                <span title={`${q.readingCells} reading · ${q.assessmentCells} assessment`}>{q.cells}</span>
            ),
        },
        {
            key: 'disputed',
            header: 'Disputed',
            sortable: true,
            className: 'bench-num',
            render: q => (q.comparable === 0
                ? <span className="bench-muted" title="No solution here has been assessed by two models, so nothing can disagree.">—</span>
                : <span className={q.disputed ? 'bench-disputed' : ''}>{q.disputed}/{q.comparable}</span>),
        },
        {
            key: 'human',
            header: 'Your verdicts',
            sortable: true,
            render: q => <TallyBar tally={q.human} of={q.cells} />,
        },
        {
            key: 'judge',
            header: 'Judge',
            sortable: true,
            render: q => <TallyBar tally={q.judge} of={q.cells} />,
        },
    ];

    return (
        <div className="bench">
            <div className="bench-toolbar">
                <div className="bench-counts">
                    <span className="bench-stat"><strong>{questions.length}</strong> questions</span>
                    <span className="bench-stat"><strong>{totals.solutions}</strong> solutions</span>
                    <span className="bench-stat"><strong>{totals.cells}</strong> prompt executions</span>
                    <span className="bench-stat"><strong>{totals.judged}</strong> judged by you</span>
                </div>
                <div className="bench-actions">
                    <button className="btn btn-primary" onClick={() => setAdding('')}>+ Add question</button>
                </div>
            </div>

            <DataTable
                columns={columns}
                data={questions}
                rowKey={q => q.questionId}
                loading={isLoading}
                sort={sort}
                onSortChange={setSort}
                mobileCards
                {...(onOpenQuestion ? { onRowClick: (q: QuestionRollup) => onOpenQuestion(q.questionId) } : {})}
                rowMenu={(q): RowMenuItem[] => [
                    { key: 'add', label: 'Add more solutions', onSelect: () => setAdding(q.questionId) },
                ]}
                emptyMessage="No questions yet. Hand-pick one with “+ Add question”."
            />

            {adding !== null && (
                <AddRowsModal
                    datasetId={datasetId}
                    initialQuestionId={adding || undefined}
                    onClose={() => setAdding(null)}
                    onAdded={() => {
                        setAdding(null);
                        qc.invalidateQueries({ queryKey: ['benchMatrix', datasetId] });
                    }}
                />
            )}
        </div>
    );
}

/** Coverage first, then the split — "0 of 27" is the honest headline when
 *  nothing has been judged, and a bar of zero width says it badly. */
function TallyBar({ tally, of }: { tally: VerdictTally; of: number }) {
    if (!tally.n) return <span className="bench-muted">0 of {of}</span>;
    return (
        <span className="bench-tally" title={`${tally.good} good · ${tally.acceptable} acceptable · ${tally.bad} bad`}>
            <span className="bench-tally-n">{tally.n} of {of}</span>
            <span className="bench-tally-bar">
                {(['good', 'acceptable', 'bad'] as VerdictValue[]).map(v => (
                    tally[v] > 0 &&
                    <span key={v} className={`seg v-${v}`} style={{ flexGrow: tally[v] }} />
                ))}
            </span>
        </span>
    );
}

/** Sorting is the caller's job — DataTable renders the header state and emits
 *  the change. Verdict columns sort on coverage, since "how much of this is
 *  judged" is the question you sort them to answer. */
function sortQuestions(questions: QuestionRollup[], sort: SortState): QuestionRollup[] {
    const value = (q: QuestionRollup): number => {
        switch (sort.sortBy) {
            case 'solutions': return q.solutions;
            case 'users': return q.users;
            case 'prompts': return q.prompts;
            case 'models': return q.models;
            case 'cells': return q.cells;
            case 'human': return q.human.n;
            case 'judge': return q.judge.n;
            default: return q.disputed;
        }
    };
    const dir = sort.sortOrder === 'asc' ? 1 : -1;
    return [...questions].sort((a, b) => (value(a) - value(b)) * dir || b.solutions - a.solutions);
}
